// Local workspace repository: loads the nested view-model projection from normalized IndexedDB records and
// commits UI edits as ONE readwrite transaction over entity stores + outbox (atomic: both or neither).
import type { IDBPDatabase } from 'idb';
import type { Project, Task, TaskList, TrashEntry } from '@/domain/model';
import type { OutboxRecord } from '@/sync/ops';
import { composeLog } from '@/sync/activity';
import { toBlob } from '@/sync/files';
import { applyTeamOrder } from '@/domain/team';
import { diffWorkspace, type DiffContext, type DiffResult, type ListRecord, type ProjectRecord, type TaskRecord } from '@/sync/diff';
import { LocalStorageFullError, isConnectionLost, isQuotaError, reopenLocalDb, type TaskPanesDB } from './localDb';

const TRASH_MS = 30 * 864e5;
const byRank = <T extends { rank: string; id: string }>(a: T, b: T) => (a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : a.id < b.id ? -1 : 1);

export class WorkspaceStore {
  constructor(private readonly db: IDBPDatabase<TaskPanesDB>) {}

  /** Assembles projects → lists → tasks (+ 30-day trash) ordered by rank. */
  async loadProjects(now = Date.now()): Promise<Project[]> {
    const tx = this.db.transaction(['projects', 'lists', 'tasks', 'fileQueue'], 'readonly');
    const [projects, lists, tasks, queued] = await Promise.all([tx.objectStore('projects').getAll(), tx.objectStore('lists').getAll(), tx.objectStore('tasks').getAll(), tx.objectStore('fileQueue').getAll()]);
    await tx.done;
    // Files not on the server yet open from the bytes kept on this device.
    const local = new Map(queued.filter(q => q.bytes).map(q => [q.id, toBlob(q)!] as const));
    for (const t of tasks) if (t.files?.some(f => local.has(f.id))) t.files = t.files.map(f => (local.has(f.id) ? { ...f, url: localFileUrl(f.id, local.get(f.id)!) } : f));
    for (const p of projects) if (p.files?.some(f => local.has(f.id))) p.files = p.files.map(f => (local.has(f.id) ? { ...f, url: localFileUrl(f.id, local.get(f.id)!) } : f));
    const listsBy = new Map<string, ListRecord[]>(), tasksBy = new Map<string, TaskRecord[]>(), trashBy = new Map<string, TaskRecord[]>();
    for (const l of lists) (listsBy.get(l.projectId) || listsBy.set(l.projectId, []).get(l.projectId)!).push(l);
    for (const t of tasks) {
      if (t.deletedAt !== null) { if (now - t.deletedAt < TRASH_MS) (trashBy.get(t.projectId) || trashBy.set(t.projectId, []).get(t.projectId)!).push(t); continue; }
      if (t.listId) (tasksBy.get(t.listId) || tasksBy.set(t.listId, []).get(t.listId)!).push(t);
    }
    return projects.sort(byRank).map(p => ({
      id: p.id, name: p.name, notes: p.notes, ...applyTeamOrder(p.members, p.teamOrder), owner: p.owner, tagOrder: p.tagOrder, rank: p.rank, revision: p.revision, ...(p.icon ? { icon: p.icon } : {}), ...(p.accent ? { accent: p.accent } : {}),
      filterOrder: p.filterOrder || [], nicks: { ...(p.nicks || {}) }, teamOrder: p.teamOrder || [], comments: (p.comments || []).map(c => ({ ...c })), ...(p.files && p.files.length ? { files: p.files.map(f => ({ ...f })) } : {}),
      ...(p.deletedAt ? { deletedAt: p.deletedAt, deletedBy: p.deletedBy ?? null } : {}), ...(p.roles ? { roles: p.roles } : {}),
      lists: (listsBy.get(p.id) || []).sort(byRank).map<TaskList>(l => ({ id: l.id, title: l.title, rank: l.rank, revision: l.revision, tasks: (tasksBy.get(l.id) || []).sort(byRank).map(toTask) })),
      trash: (trashBy.get(p.id) || []).sort((a, b) => (a.deletedAt! - b.deletedAt!)).map<TrashEntry>(t => ({ task: toTask(t), listId: t.trashListId, listTitle: t.trashListTitle, at: t.deletedAt!, by: t.deletedBy || '' })),
    }));
  }

  /**
   * Computes operations for before→after and persists entity records + outbox records atomically.
   * Resolves only after the IndexedDB transaction committed ("saved on device").
   */
  async commit(before: Project[], after: Project[], ctx: DiffContext, retried = false): Promise<DiffResult> {
    const d = diffWorkspace(before, after, ctx);
    if (!d.ops.length && !d.projects.size && !d.lists.size && !d.tasks.size) return d;
    try {
      const tx = this.db.transaction(['projects', 'lists', 'tasks', 'outbox'], 'readwrite');
      const ps = tx.objectStore('projects'), ls = tx.objectStore('lists'), ts = tx.objectStore('tasks'), ob = tx.objectStore('outbox');
      const w: Array<Promise<unknown>> = [];
      // v10.1: project comment images belong to the file queue and server file entities, never to a UI copy.
      for (const [id, r] of d.projects) w.push(r ? ps.get(id).then(ex => ps.put({ ...r, files: ((ex as ProjectRecord | undefined)?.files || []).map(f => ({ ...f, url: f.pending || f.error ? undefined : f.url })) })) : ps.delete(id));
      for (const [id, r] of d.lists) w.push(r ? ls.put(r) : ls.delete(id));
      // The raw server activity log (`slog`) is owned by sync: keep the stored copy, never a stale UI one.
      for (const [id, r] of d.tasks) w.push(r ? ts.get(id).then(ex => ts.put(withServerLog(stripTask(r), ex as TaskRecord | undefined))) : ts.delete(id));
      // Declare dependencies on creates that are still unacknowledged in the outbox (earlier commits), so the
      // server answers dependency_pending rather than rejecting a reference to a not-yet-created entity.
      const pendingCreates = new Map<string, string>();
      for (const r of await ob.getAll()) if (/^create/.test(r.op.command_type) || r.op.command_type === 'addChecklistItem') pendingCreates.set(r.op.command_type === 'addChecklistItem' ? String(r.op.payload.item_id) : r.op.entity_id, r.op.operation_id);
      // Dependency-safe cancellation: undoing a creation whose create was never sent removes the create and
      // every later unsent operation on (or depending on) that entity instead of sending create+delete.
      const cancelled = new Set<string>();
      for (const op of [...d.ops]) {
        if (op.command_type !== 'deleteTask' || !(op.payload as { undo_create?: boolean }).undo_create) continue;
        const queued = (await ob.getAll()).filter(r => r.op.entity_id === op.entity_id);
        const create = queued.find(r => r.op.command_type === 'createTask');
        if (!create || create.state !== 'pending') { delete (op.payload as { undo_create?: boolean }).undo_create; continue; }
        const drop = new Set(queued.map(r => r.op.operation_id));
        for (const r of await ob.getAll()) if (r.op.depends_on.some(x => drop.has(x))) drop.add(r.op.operation_id);
        for (const r of await ob.getAll()) if (drop.has(r.op.operation_id) && r.state === 'pending') await ob.delete(r.seq!);
        cancelled.add(op.operation_id);
      }
      d.ops = d.ops.filter(o => !cancelled.has(o.operation_id));
      for (const op of d.ops) {
        const refs = [op.entity_id, ...['project_id', 'list_id', 'after_id', 'before_id', 'target_list_id', 'item_id'].map(k => op.payload[k] as string | null | undefined)];
        for (const id of refs) { const dep = id ? pendingCreates.get(id) : undefined; if (dep && dep !== op.operation_id && !op.depends_on.includes(dep)) op.depends_on.push(dep); }
        if (/^create/.test(op.command_type)) pendingCreates.set(op.entity_id, op.operation_id);
        w.push(ob.add({ op, state: 'pending', attempts: 0, next_attempt_at: 0 } satisfies OutboxRecord));
      }
      await Promise.all([...w, tx.done]);
    } catch (e) {
      if (isQuotaError(e)) throw new LocalStorageFullError(e);
      // The transaction is atomic: nothing was written. A lost connection is reopened and the commit retried once.
      if (!retried && isConnectionLost(e)) { await reopenLocalDb(this.db); return this.commit(before, after, ctx, true); }
      console.error('[tp-db] local commit failed', e);
      throw e;
    }
    return d;
  }

  /**
   * Queues one allowlisted operation that has no local entity counterpart to diff against (e.g. restoreProject of
   * a project this device no longer holds, from the Trash pane or Undo). Durable before it counts as saved.
   */
  async enqueue(op: OutboxRecord['op'], retried = false): Promise<void> {
    try {
      const tx = this.db.transaction('outbox', 'readwrite');
      await Promise.all([tx.store.add({ op, state: 'pending', attempts: 0, next_attempt_at: 0 } satisfies OutboxRecord), tx.done]);
    } catch (e) {
      if (isQuotaError(e)) throw new LocalStorageFullError(e);
      if (!retried && isConnectionLost(e)) { await reopenLocalDb(this.db); return this.enqueue(op, true); }
      console.error('[tp-db] local enqueue failed', e);
      throw e;
    }
  }

  async pendingCount(): Promise<number> { return this.db.countFromIndex('outbox', 'byState', 'pending').then(async n => n + await this.db.countFromIndex('outbox', 'byState', 'inflight')); }
  async outbox(): Promise<OutboxRecord[]> { return this.db.getAll('outbox'); }
  async getMeta<T>(key: string): Promise<T | undefined> { return this.db.get('meta', key) as Promise<T | undefined>; }
  async setMeta(key: string, value: unknown): Promise<void> { await this.db.put('meta', value, key); }

  /** Export of unsynced work (no secrets) for quota/logout recovery. */
  async exportUnsynced(): Promise<string> {
    const ops = await this.outbox();
    return JSON.stringify({ format: 'taskpanes-unsynced-v1', exported_at: new Date().toISOString(), operations: ops.filter(o => o.state !== 'rejected' || o.result).map(o => ({ state: o.state, op: o.op, last_error: o.last_error })) }, null, 2);
  }
}

function toTask(r: TaskRecord): Task {
  const { projectId: _p, listId: _l, deletedAt: _d, deletedBy: _b, trashListId: _tl, trashListTitle: _tt, ...t } = r;
  return t;
}
function withServerLog(r: TaskRecord, stored: TaskRecord | undefined): TaskRecord {
  // Attachments are owned by the file queue and server `file` entities (P12), never by a (possibly stale) UI copy.
  const files = stored ? (stored.files || []).map(f => ({ ...f, url: f.pending || f.error ? undefined : f.url })) : (r.files || []).filter(f => !f.url?.startsWith('blob:'));
  const slog = stored?.slog ?? r.slog;
  if (!slog) return { ...r, files };
  return { ...r, files, slog, log: composeLog(slog, r.log, true) };
}
const blobUrls = new Map<string, string>();
function localFileUrl(id: string, blob: Blob): string {
  let u = blobUrls.get(id);
  if (!u) { u = URL.createObjectURL(blob); blobUrls.set(id, u); }
  return u;
}
function stripTask(r: TaskRecord): TaskRecord {
  // Nested objects come from reactive view-model state; store plain structured-cloneable copies.
  return JSON.parse(JSON.stringify(r));
}

export type { ProjectRecord };
